import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import DialogConfirmation from "components/DesignComponents/DialogConfirmation";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import DropzoneContext from "context/dropzone/dropzoneContext";
import TableContext from "context/table/tableContext";
import TriSourceContext from "context/triSource/triSourceContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { TRI_SOURCES } from "shared/types/enums";

import { TriSourceConfirmationDialogProps } from "../../types";

const TriSourceConfirmationDialog = ({
    isOpenMainDialog,
    description,
    onSubmit
}: TriSourceConfirmationDialogProps) => {
    const { tableName, selectedRows } = useContext(TableContext);
    const { lineCount } = useContext(DropzoneContext);

    const {
        activeSource,
        filteredOnlyCount,
        showConfirmation,
        handleShowConfirmation
    } = useContext(TriSourceContext);

    const { t } = useTranslation();
    const { renderFormattedNumber } = useFormattedNumber();

    const [enteredNumber, setEnteredNumber] = useState("");
    const [isConfirmed, setIsConfirmed] = useState(false);

    const closeDialog = () => handleShowConfirmation(false);

    const getCount = () => {
        if (activeSource === TRI_SOURCES.Selected) {
            return selectedRows.length;
        }

        if (activeSource === TRI_SOURCES.Filtered) {
            return Number(filteredOnlyCount);
        }

        return lineCount;
    };

    const handleEnteredNumberChange = (e: { target: { value: any } }) => {
        const count = getCount();
        const value = e.target.value.trim();

        setEnteredNumber(value);
        setIsConfirmed(value === String(count));
    };

    const handleSubmit = () => {
        onSubmit?.();
        handleShowConfirmation(false);
    };

    const getTitleAndLabel = () => {
        const count = getCount();

        if (activeSource === TRI_SOURCES.FromFile) {
            return {
                tagTitle: `${t(
                    "Dialog##confirmations##number of lines"
                )}: ${renderFormattedNumber(count)}`,
                inputLabel: t("Dialog##confirmations##file")
            };
        } else {
            const selectedOrFiltered =
                activeSource === TRI_SOURCES.Selected ? "selected" : "filtered";

            return {
                tagTitle: `${t(
                    `Dialog##${selectedOrFiltered} ${tableName}`
                )}: ${renderFormattedNumber(count)}`,
                inputLabel: t(
                    `Dialog##confirmations##${activeSource}##${tableName}`
                )
            };
        }
    };

    const { tagTitle, inputLabel } = getTitleAndLabel();

    const fullDescription = `${description} ${t(
        "Dialog##confirmations##description second part"
    )}`;

    const handleOnExited = () => {
        setEnteredNumber("");
        setIsConfirmed(false);
    };

    return (
        <DialogConfirmation
            isOpen={showConfirmation && isOpenMainDialog}
            description={fullDescription}
            onClose={closeDialog}
            onSubmit={handleSubmit}
            onExited={handleOnExited}
            isConfirmBtnDisabled={!isConfirmed}
            confirmBtnTestId="confirm-create-task-button"
        >
            <Tag
                size={TAG_SIZES.Medium}
                color={TAG_COLORS.Primary}
                title={tagTitle}
            />

            <InputField
                fullWidth
                required
                size={INPUT_FIELD_SIZES.Medium}
                value={enteredNumber}
                placeholder="0"
                onChange={handleEnteredNumberChange}
                labelLeft={inputLabel}
                customStyle={{
                    marginTop: "8px"
                }}
            />
        </DialogConfirmation>
    );
};

export default TriSourceConfirmationDialog;
